import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiUrl from "../../../Environment/Api";

function Update_TaskManagement() {
  const navigate = useNavigate();
  const { id } = useParams();

  // today date
  const today = new Date().toISOString().split("T")[0];

  // Modal state
  const [clientData, setClientData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalInputValue, setModalInputValue] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);
  const [notes, setNotes] = useState("");

  const UserName = localStorage.getItem("name");
  const UserId = localStorage.getItem("editor_id");
  const UserRole = localStorage.getItem("role");

  // Dynamic columns
  const [columns, setColumns] = useState([
    "Standard",
    "Premier",
    "Twilight",
    "DTD",
    "VRA",
    "VR",
  ]);
  const MAX_COLUMNS = 10;

  const defaultColumnNames = [
    "Standard",
    "Premier",
    "Twilight",
    "DTD",
    "VRA",
    "VR",
    "Col7",
    "Col8",
    "Col9",
    "Col10",
  ];

  // Table data
  const [tableData, setTableData] = useState([]);

  // Fetch Clients
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${apiUrl}/clientdetails`);
      setClientData(response.data || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  // Fetch Task for Update
  const fetchTask = async () => {
    try {
      const res = await axios.get(`${apiUrl}/taskmanagement/${id}`);
      const data = res.data;

      setNotes(data.notes || "");

      // Reconstruct rows with dynamic columns
      const rows = data.client_details.map((item, index) => {
        let row = {
          id: Date.now() + index,
          taskDate: item.date || today,
          address: item.address || "",
          clientAddress: item.client_address || "",
          countrycode: item.countrycode || "",
          clientId: item.client_id || "",
        };

        // Map existing column values
        columns.forEach((col, i) => {
          row[`col_${i}`] = item[col.toLowerCase()] || "";
        });

        return row;
      });

      setTableData(
        rows.length > 0
          ? rows
          : [
              {
                id: Date.now(),
                taskDate: today,
                address: "",
                clientId: "",
              },
            ],
      );
    } catch (err) {
      console.log(err);
      toast.error("Failed to load task data");
    }
  };

  useEffect(() => {
    fetchClients();
    fetchTask();
  }, [id]);

  // Handle Add Row
  const handleAddRow = () => {
    const newRow = {
      id: Date.now(),
      taskDate: today,
      address: "",
      clientId: "",
    };

    columns.forEach((_, index) => {
      newRow[`col_${index}`] = "";
    });

    setTableData([...tableData, newRow]);
  };

  // Handle Input Change
  const handleInputChange = (index, field, value) => {
    const updatedData = [...tableData];
    updatedData[index][field] = value;
    setTableData(updatedData);
  };

  // Handle Delete Row
  const handleDeleteRow = (id) => {
    setTableData(tableData.filter((row) => row.id !== id));
  };

  // Handle Clear All
  const handleClearAll = () => {
    setTableData([
      {
        id: Date.now(),
        taskDate: today,
        address: "",
        clientId: "",
      },
    ]);
    setNotes("");
    // toast.info("All tasks cleared", { autoClose: 2000 });
  };

  // ==================== COLUMN MANAGEMENT ====================

  const openAddColumnModal = () => {
    const nextIndex = columns.length;
    setModalTitle("Add New Column");
    setModalInputValue(defaultColumnNames[nextIndex] || "");
    setIsEditMode(false);
    setPendingIndex(null);
    setShowModal(true);
  };

  const openEditColumnModal = (index) => {
    setModalTitle("Edit Column Name");
    setModalInputValue(columns[index]);
    setIsEditMode(true);
    setPendingIndex(index);
    setShowModal(true);
  };

  const handleModalSave = () => {
    if (!isEditMode && columns.length >= MAX_COLUMNS) {
      toast.error("Maximum 10 columns allowed");
      return;
    }

    if (isEditMode && pendingIndex !== null) {
      // Edit existing column
      const updatedColumns = [...columns];
      updatedColumns[pendingIndex] =
        modalInputValue || defaultColumnNames[pendingIndex];
      setColumns(updatedColumns);
    } else {
      // Add new column
      const newIndex = columns.length;
      const newColumnName = modalInputValue || defaultColumnNames[newIndex];

      setColumns([...columns, newColumnName]);

      // Add new column to all existing rows
      setTableData((prevData) =>
        prevData.map((row) => ({
          ...row,
          [`col_${newIndex}`]: "",
        })),
      );
    }

    setShowModal(false);
    setModalInputValue("");
    setPendingIndex(null);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setModalInputValue("");
    setPendingIndex(null);
  };

  const handleRemoveColumn = (index) => {
    const updatedColumns = columns.filter((_, i) => i !== index);
    setColumns(updatedColumns);

    setTableData((prevData) =>
      prevData.map((row) => {
        const newRow = { ...row };

        updatedColumns.forEach((_, i) => {
          const oldKey = `col_${i >= index ? i + 1 : i}`;
          newRow[`col_${i}`] = row[oldKey] || "";
        });

        // Remove old extra columns
        Object.keys(newRow).forEach((key) => {
          if (
            key.startsWith("col_") &&
            parseInt(key.split("_")[1]) >= updatedColumns.length
          ) {
            delete newRow[key];
          }
        });

        return newRow;
      }),
    );
  };

  // Handle Back
  const handleBack = () => {
    navigate(-1);
  };

  // Handle Update
  const handleUpdate = async () => {
    try {
      const clientDetails = tableData.map((row) => {
        const clientInfo = clientData.find((c) => c.client_id === row.clientId);

        let obj = {
          client_id: row.clientId || "",
          client_name: clientInfo?.clientName || "",
          client_address: row.clientAddress || "",
          countrycode: row.countrycode || "",
          address: row.address || "",
          date: row.taskDate,
        };

        columns.forEach((col, index) => {
          obj[col.toLowerCase()] = row[`col_${index}`] || 0;
        });

        return obj;
      });

      const payload = {
        editor_id: UserId === "undefined" ? "admin" : UserId,
        editor_name: UserName,
        date: today,
        notes: notes,
        client_details: clientDetails,
      };

      console.log("UPDATE PAYLOAD:", payload);

      await axios.put(`${apiUrl}/taskmanagement/${id}`, payload);

      toast.success("Task Updated Successfully!");
      navigate("/task-management");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update task");
    }
  };

  return (
    <>
      <ToastContainer />

      {/* Modal - Same as Add */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalTitle}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleModalCancel}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Column Name:</label>
                <input
                  type="text"
                  className="form-control"
                  value={modalInputValue}
                  onChange={(e) => setModalInputValue(e.target.value)}
                  placeholder="Enter column name"
                  autoFocus
                  onKeyPress={(e) => e.key === "Enter" && handleModalSave()}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleModalCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleModalSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      {UserRole === "editor" ? (
        <div className="d-flex justify-content-between">
          <div>
            <h3>Update Task Management</h3>
          </div>
          <div className="d-flex flex-column">
            <div>
              <span>Name :</span>
              <span className="ms-2">{UserName}</span>
            </div>
            <div>
              <span>ID :</span>
              <span className="ms-2">{UserId}</span>
            </div>
          </div>
        </div>
      ) : (
        <h3 className="text-center">Update Task Management</h3>
      )}

      <hr />

      <div className="bg-white p-3 rounded-3 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Tasks</h5>
          <div>
            <button
              type="button"
              className="btn btn-success btn-sm me-2"
              onClick={handleAddRow}
            >
              <i className="fa-solid fa-plus me-1"></i> Add Task
            </button>
            {columns.length < MAX_COLUMNS && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={openAddColumnModal}
              >
                <i className="fa-solid fa-plus me-1"></i> Add Column
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ width: "100%", overflowX: "auto" }}>
          <table
            className="table table-bordered"
            style={{
              minWidth: "1200px",
              tableLayout: "fixed",
              borderCollapse: "collapse",
            }}
          >
            <thead className="font-size">
              <tr>
                <th style={{ width: "30px" }}>S.No</th>
                <th style={{ width: "145px" }}>Client ID / Name</th>
                <th style={{ width: "120px" }}>Date</th>
                <th style={{ width: "130px", display: "none" }}>
                  Client Address
                </th>
                <th style={{ width: "130px" }}>Address</th>

                {/* Dynamic Columns */}
                {columns.map((col, index) => (
                  <th key={index} style={{ width: "70px" }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => openEditColumnModal(index)}
                      >
                        {col}
                      </span>
                      <div>
                        <i
                          className="fa-solid fa-edit me-2"
                          style={{ cursor: "pointer", fontSize: "13px" }}
                          onClick={() => openEditColumnModal(index)}
                          title="Edit"
                        ></i>
                        <i
                          className="fa-solid fa-trash text-danger"
                          style={{ cursor: "pointer", fontSize: "13px" }}
                          onClick={() => handleRemoveColumn(index)}
                          title="Remove"
                        ></i>
                      </div>
                    </div>
                  </th>
                ))}
                <th style={{ width: "60px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={row.id}>
                  <th scope="row">{index + 1}</th>

                  <td>
                    <select
                      className="form-select"
                      value={row.clientId || ""}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedClient = clientData.find(
                          (c) => c.client_id === selectedId,
                        );

                        const updatedData = [...tableData];
                        updatedData[index].clientId = selectedId;
                        // updatedData[index].address =
                        //   selectedClient?.address || "";

                        updatedData[index].clientAddress =
                          selectedClient?.countrycode || "";

                        updatedData[index].countrycode =
                          selectedClient?.address || "";

                        // Auto add new row when selecting client in last row
                        if (index === tableData.length - 1 && selectedId) {
                          const newRow = {
                            id: Date.now(),
                            taskDate: today,
                            address: "",
                            clientId: "",
                          };
                          columns.forEach((_, i) => {
                            newRow[`col_${i}`] = "";
                          });
                          updatedData.push(newRow);
                        }

                        setTableData(updatedData);
                      }}
                    >
                      <option value="">-- Select Client --</option>
                      {clientData.map((data, idx) => (
                        <option key={idx} value={data.client_id}>
                          {data.client_id} - {data.alternat_name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={row.taskDate || today}
                      onChange={(e) =>
                        handleInputChange(index, "taskDate", e.target.value)
                      }
                    />
                  </td>

                  <td style={{ display: "none" }}>
                    <input
                      style={{ width: "100%" }}
                      className="form-control"
                      type="text"
                      value={row.clientAddress || ""}
                      readOnly
                    />
                  </td>

                  <td style={{ display: "none" }}>
                    <input
                      style={{ width: "100%" }}
                      className="form-control"
                      type="text"
                      value={row.countrycode || ""}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      style={{ width: "100%" }}
                      className="form-control"
                      type="text"
                      value={row.address || ""}
                      onChange={(e) =>
                        handleInputChange(index, "address", e.target.value)
                      }
                      placeholder="Enter address manually"
                    />
                  </td>

                  {/* Dynamic Column Inputs */}
                  {columns.map((col, colIndex) => (
                    <td key={colIndex}>
                      <input
                        type="text"
                        className="form-control"
                        value={row[`col_${colIndex}`] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            `col_${colIndex}`,
                            e.target.value,
                          )
                        }
                      />
                    </td>
                  ))}

                  <td>
                    <button
                      className="btn btn-danger px-2 py-1"
                      onClick={() => handleDeleteRow(row.id)}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes Section */}
        <div className="row mt-4">
          <div className="col-lg-6">
            <label className="form-label">
              <b>Notes / Remarks :</b>
            </label>
            <textarea
              className="form-control notes-textarea"
              rows="4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes here..."
            ></textarea>
          </div>
        </div>

        {/* Bottom Buttons - Same as Add */}
        <div className="d-flex justify-content-end align-items-center my-3">
          <div className="d-flex gap-3 justify-content-evenly mt-3 buttonstyle">
            <button
              type="button"
              className="btn btn-danger invoice-button"
              onClick={handleClearAll}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-info text-white invoice-button"
              style={{ backgroundColor: "#007BFF" }}
              onClick={handleBack}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn-success invoice-button"
              onClick={handleUpdate}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Update_TaskManagement;
